import { BackendError, backendFetch, CSRF_COOKIE_NAME, getCookie } from "@/lib/backend";

export interface ReportDocument {
  id: string;
  original_filename: string;
  content_type: string | null;
  size_bytes: number;
  available: boolean;
}
export interface ReportAttempt {
  id: string;
  report_id: string;
  idempotency_key: string;
  brief_revision: number;
  status: "submitting" | "active" | "completed" | "failed";
  outcome: "generated" | "not_created" | "replied" | "needs_input" | null;
  continuation_id: string | null;
  message: string | null;
  error: string | null;
  result_version_id: string | null;
  report_sheet_names: string[];
  source_sheet_names: string[];
}
export interface ReportChatTurn extends ReportAttempt {
  user_message: string;
  attachments: Pick<ReportDocument, "id" | "original_filename">[];
}
export interface AgentReport {
  id: string;
  name: string;
  instructions: string;
  revision: number;
  documents: ReportDocument[];
  latest_attempt: ReportAttempt | null;
  last_successful_result: ReportAttempt | null;
  needs_regeneration: boolean;
  available_in_version: boolean;
  version_id: string | null;
}
export interface ReportBrief {
  name: string;
  instructions: string;
  document_ids: string[];
}
export interface ReportCapabilities {
  supported_extensions: string[];
  max_file_bytes: number;
  name_max_length: number;
  instructions_max_length: number;
}

function base(projectId: string) {
  return `/api/projects/${encodeURIComponent(projectId)}/databook/agent-reports`;
}
async function request<T>(path: string, method = "GET", body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  const csrf = getCookie(CSRF_COOKIE_NAME);
  if (csrf && method !== "GET") headers["x-csrf-token"] = csrf;
  const multipart = body instanceof FormData;
  if (body !== undefined && !multipart) headers["content-type"] = "application/json";
  const response = await backendFetch(path, {
    method, headers,
    body: body === undefined ? undefined : multipart ? body : JSON.stringify(body),
  });
  if (!response.ok) throw new BackendError("The report request failed.", {
    status: response.status, bodyText: await response.text(),
  });
  return response.status === 204 ? undefined as T : response.json();
}
export const reportIsRunning = (report: AgentReport) =>
  report.latest_attempt?.status === "submitting" || report.latest_attempt?.status === "active";
export function reportStatus(report: AgentReport): string {
  const attempt = report.latest_attempt;
  if (attempt?.status === "submitting") return "Starting…";
  if (attempt?.status === "active") return "Working…";
  if (attempt?.status === "failed") return "Failed";
  if (attempt?.outcome === "needs_input") return "Awaiting reply";
  if (attempt?.outcome === "replied" && !report.last_successful_result) return "Conversation";
  if (attempt?.outcome === "not_created") return "Not created";
  if (report.last_successful_result) return report.needs_regeneration ? "Needs update" : "Ready";
  return "Not started";
}
export function reportRequestKey(): string {
  // getRandomValues also works on the HTTP dev server, unlike randomUUID.
  return Array.from(crypto.getRandomValues(new Uint32Array(4)), n => n.toString(16).padStart(8, "0")).join("");
}
export const listAgentReports = (projectId: string, versionId?: string) =>
  request<AgentReport[]>(base(projectId) + (versionId ? `?version_id=${encodeURIComponent(versionId)}` : ""));
export const getAgentReport = (projectId: string, id: string) => request<AgentReport>(`${base(projectId)}/${id}`);
export const getReportCapabilities = (projectId: string) => request<ReportCapabilities>(`${base(projectId)}/capabilities`);
export const createAgentReport = (projectId: string, brief: ReportBrief, key: string) =>
  request<AgentReport>(base(projectId), "POST", { ...brief, idempotency_key: key });
export const archiveAgentReport = (projectId: string, id: string) => request<void>(`${base(projectId)}/${id}`, "DELETE");
export function attachReportFile(projectId: string, report: AgentReport, file: File) {
  const form = new FormData();
  form.append("file", file);
  form.append("expected_revision", String(report.revision));
  return request<{ document: ReportDocument; reused: boolean; revision: number }>(`${base(projectId)}/${report.id}/attachments`, "POST", form);
}
export const runAgentReport = (projectId: string, report: AgentReport, key: string, userMessage: string, attachmentDocumentIds: string[]) =>
  request<ReportAttempt>(`${base(projectId)}/${report.id}/runs`, "POST", { expected_revision: report.revision, idempotency_key: key, user_message: userMessage, attachment_document_ids: attachmentDocumentIds });
export const getReportMessages = (projectId: string, id: string) => request<ReportChatTurn[]>(`${base(projectId)}/${id}/messages`);
export const getReportAttempt = (projectId: string, attempt: ReportAttempt) =>
  request<ReportAttempt>(`${base(projectId)}/${attempt.report_id}/runs/${attempt.id}`);
